<template>
  <section
    v-if="slides.length && currentSlide"
    class="bg-surface-brand-hi relative flex gap-8 rounded-[32px] min-h-[242px]"
    data-test="condensed-funnel-grades-carousel"
  >
    <!-- Left column: tabs top-left, guidance chips bottom-left -->
    <div
      class="flex min-w-0 flex-1 flex-col justify-between gap-16 ps-16 py-16"
    >
      <TabSwitch
        v-if="tabs.length > 1"
        :active-id="currentSlideIndex"
        :label="i18n(translations.alternativeController)"
        :tabs
        @update:active-id="onTabChange"
      />

      <div
        v-if="showTags && currentSlide.tags?.length"
        class="flex flex-col items-start gap-8"
        data-test="condensed-funnel-grades-carousel-tags"
      >
        <AnimatedGuidanceChip
          v-for="(tag, tagIndex) in currentSlide.tags"
          :key="tag.id || tagIndex"
          :index="tagIndex"
          :tag
        />
      </div>
    </div>

    <!-- Right column: image bordering the bottom right corner -->
    <div
      class="relative min-w-0 flex-1"
      :aria-label="currentSlide.title"
      role="tabpanel"
    >
      <ExampleImage
        class="bg-[#1C1C1CB2] absolute right-16 top-16 z-10 px-4 text-static-light-hi backdrop-blur-sm rounded-full"
        :with-icon="false"
      />

      <RevIllustration
        ref="illustration"
        :alt="currentSlide.title || ''"
        class="absolute inset-0 h-full w-full object-contain object-right-bottom rounded-[32px]"
        :height="498"
        :src="currentSlide.imageUrl"
        :width="498"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import type { Picker } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import type { EventData } from '@backmarket/nuxt-module-tracking'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'

import translations from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ConditionGuidance/ConditionGuidance.translations'
import AnimatedGuidanceChip from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ConditionGuidance/components/AnimatedGuidanceChip/AnimatedGuidanceChip.vue'
import ExampleImage from '~/scopes/product-core/components/presentationals/ExampleImage/ExampleImage.vue'
import TabSwitch from '~/scopes/product-core/components/presentationals/TabSwitch/TabSwitch.vue'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

const props = withDefaults(
  defineProps<{
    trackingModel?: string
    brand?: string
    content?:
      | NonNullable<NonNullable<Picker['sideContentV2']>['content']>
      | undefined
    showTags?: boolean
  }>(),
  {
    trackingModel: '',
    brand: '',
    content: undefined,
    showTags: true,
  },
)

const { trackClick } = useTracking()
const { grade } = useUrlParams()
const i18n = useI18n()

const currentSlideIndex = ref(0)

const slides = computed(() => {
  return (
    props.content?.find((entry) => entry.grade === grade.value)?.items || []
  )
})

const currentSlide = computed(() => {
  return slides.value[currentSlideIndex.value]
})

const tabs = computed(() => {
  return slides.value.map((slide, index) => ({
    id: index,
    label: slide.title,
  }))
})

watch(
  grade,
  () => {
    currentSlideIndex.value = 0
  },
  { immediate: false },
)

function onTabChange(id: number) {
  if (id === currentSlideIndex.value) {
    return
  }

  currentSlideIndex.value = id
  trackCarousel('thumbnail_carousel')
}

function trackCarousel(name: string): void {
  const trackingValues: EventData = {
    name,
    zone: 'pp_step_grade_carousel',
    product_model: props.trackingModel,
  }

  if (grade.value) {
    trackingValues.current_grade = grade.value
  }

  trackClick(trackingValues)
}
</script>
